import { RemoveCircle } from '@mui/icons-material'
import {
    Box,
    Button,
    IconButton,
    Paper,
    Stack,
    TablePagination,
    Typography,
} from '@mui/material'
import {
    DataGridPro,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridColDef,
    GridFooterContainer,
    GridPreferencePanelsValue,
    GridRowSelectionModel,
    GridSortModel,
    useGridApiContext,
    useGridApiRef,
} from '@mui/x-data-grid-pro'
import {
    GetRostersByIdsQuery,
    type RosterProfileQuery,
    SortIndexField,
} from '__generated__/graphql'
import { type FC, useEffect, useMemo, useState } from 'react'
import ProcessStatusPill from 'recruiter/components/ProcessStatusPill'
import { useBulkActionContext } from 'recruiter/hooks/useBulkActionContext'
import { useRosterCandidatesColumns } from 'recruiter/hooks/useRosterCandidatesColumns'
import {
    type PaginationState,
    type SortState,
} from 'shared/hooks/candidateSearchUrlState'
import {
    SORT_INDEX_TO_FIELD,
    FIELD_TO_SORT_INDEX,
} from 'recruiter/components/CandidateSearchResultsTable'
import { type RosterCandidateTableRowModel } from 'recruiter/contexts/CandidateSqlSearchContext'
import { getPxAsRem } from 'shared/utils'
import RosterBulkActions from 'recruiter/pages/Rosters/RosterProfile/BulkActions'
import { NameColumnHeaderSql } from 'recruiter/pages/Rosters/RosterProfile/Search/NameColumnHeaderSql'
import { RosterCandidateTableViewId } from 'recruiter/pages/Rosters/RosterProfile/Search/RosterSearchProfile'

type RefColumnRoster = NonNullable<GetRostersByIdsQuery['rostersByIds']>[number]

export const generateRowId = ({
    id,
    rosterCandidateId,
}: {
    id: string
    rosterCandidateId?: string | null
}) => `${id}${rosterCandidateId ? `/${rosterCandidateId}` : ''}`

function parseSelectedRowIds(rowIds: string[]): [string[], string[]] {
    return rowIds.reduce<[string[], string[]]>(
        (acc, rowId) => {
            const [id, rosterCandidateId] = rowId.split('/')
            acc[0].push(id)
            if (rosterCandidateId) {
                acc[1].push(rosterCandidateId)
            }
            return acc
        },
        [[], []]
    )
}

interface FooterSlotProps {
    page: number
    limit: number
    totalItems: number
    onPageChange: (page: number) => void
    onLimitChange: (limit: number) => void
}

const FooterSlot: FC<FooterSlotProps> = ({
    page,
    limit,
    totalItems,
    onPageChange,
    onLimitChange,
}) => {
    const apiRef = useGridApiContext()
    const allColumns = apiRef.current.getAllColumns()
    const visibleColumns = apiRef.current.getVisibleColumns()
    const showCount = visibleColumns.length < allColumns.length

    return (
        <GridFooterContainer>
            <Button
                variant="text"
                size="small"
                onClick={() =>
                    apiRef.current.showPreferences(
                        GridPreferencePanelsValue.columns
                    )
                }
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
                Columns
                {showCount &&
                    ` (${visibleColumns.length}/${allColumns.length})`}
            </Button>
            <Box flex={1} />
            <TablePagination
                component="div"
                count={totalItems}
                page={page - 1}
                rowsPerPage={limit}
                onPageChange={(_, newPage) => {
                    if (totalItems > 0) {
                        onPageChange(newPage + 1)
                    }
                }}
                onRowsPerPageChange={(e) => {
                    onLimitChange(parseInt(e.target.value, 10))
                }}
                rowsPerPageOptions={[25, 50, 100]}
            />
        </GridFooterContainer>
    )
}

interface ToolbarSlotProps {
    loading: boolean
    selectionCount: number
    totalCount: number
    roster: RosterProfileQuery['roster']
    currentView: RosterCandidateTableViewId
    selectedCandidateIds: string[]
    selectedRosterCandidateIds: string[]
    apiRef: ReturnType<typeof useGridApiRef>
}

const ToolbarSlot: FC<ToolbarSlotProps> = ({
    loading,
    selectionCount,
    totalCount,
    roster,
    currentView,
    selectedCandidateIds,
    selectedRosterCandidateIds,
    apiRef,
}) => {
    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={getPxAsRem(8)}
            paddingBlockStart={getPxAsRem(6)}
            paddingBlockEnd={getPxAsRem(12)}
        >
            <Typography fontSize="0.8125rem" color="text.secondary">
                {loading
                    ? 'Fetching results...'
                    : selectionCount > 0
                      ? `${selectionCount} selected of ${totalCount} results`
                      : `${totalCount} results`}
            </Typography>

            <Box flex={1} />

            <RosterBulkActions
                hasEditAccess={roster?.permissions?.canEdit}
                currentView={currentView}
                rosterId={roster?.id || ''}
                selectedCandidateIds={selectedCandidateIds}
                selectedRosterCandidateIds={selectedRosterCandidateIds}
                apiRef={apiRef}
            />
        </Stack>
    )
}

interface RosterCandidatesSearchResultsTableProps {
    roster: RosterProfileQuery['roster']
    currentView: RosterCandidateTableViewId
    data: RosterCandidateTableRowModel[]
    loading: boolean
    totalItems?: number
    page: PaginationState['page']
    limit: PaginationState['limit']
    sort: SortState
    refColumnRosters: RefColumnRoster[]
    onRefColumnRosterRemove: (rosterId: string) => void
    onSortChange: (sort: SortState) => void
    onPaginationChange: (model: { page: number; pageSize: number }) => void
    filtersKey?: string
}

export function RosterCandidatesSearchResultsTable({
    roster,
    currentView,
    data,
    loading,
    totalItems = 0,
    page,
    limit,
    sort,
    refColumnRosters,
    onRefColumnRosterRemove,
    onSortChange,
    onPaginationChange,
    filtersKey,
}: RosterCandidatesSearchResultsTableProps) {
    const apiRef = useGridApiRef()
    const [selectedRowIds, setSelectedRowIds] = useState<string[]>([])

    useEffect(() => {
        setSelectedRowIds([])
    }, [filtersKey])

    const { isLoading: bulkActionLoading } = useBulkActionContext()
    const { columnVisibilityModel, columnDef } = useRosterCandidatesColumns()

    const [selectedCandidateIds, selectedRosterCandidateIds] = useMemo(
        () => parseSelectedRowIds(selectedRowIds),
        [selectedRowIds]
    )

    const referenceRosterColumns: GridColDef<RosterCandidateTableRowModel>[] =
        useMemo(
            () =>
                refColumnRosters.map((rr) => ({
                    field: `referenceRosterProcessStatus-${rr.id}`,
                    headerName: rr.rosterName,
                    editable: false,
                    sortable: false,
                    width: 150,
                    renderCell: (params) => {
                        const refRosterCandidate =
                            params.row.referenceRosterCandidates?.find(
                                (rrc) => rrc.rosterId === rr.id
                            )

                        return !refRosterCandidate ? (
                            '-'
                        ) : (
                            <ProcessStatusPill
                                status={
                                    refRosterCandidate?.processStatus ??
                                    undefined
                                }
                                statusLabel={
                                    refRosterCandidate?.processStatusLabel ??
                                    'N/A'
                                }
                                summaryGroup={
                                    refRosterCandidate?.summaryGroup ??
                                    undefined
                                }
                                summaryGroupLabel={
                                    refRosterCandidate?.summaryGroupLabel ??
                                    undefined
                                }
                            />
                        )
                    },
                    renderHeader: () => (
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            flexGrow={1}
                            gap={1}
                            width="100%"
                        >
                            <Typography
                                sx={{
                                    flexShrink: 1,
                                    overflow: 'hidden',
                                    textWrap: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 'calc(100% - 48px)',
                                }}
                                variant="subtitle2"
                            >
                                {rr.rosterName}
                            </Typography>
                            <IconButton
                                onClick={() => onRefColumnRosterRemove(rr.id)}
                            >
                                <RemoveCircle color="error" />
                            </IconButton>
                        </Stack>
                    ),
                })),
            [refColumnRosters, onRefColumnRosterRemove]
        )

    const columnsWithSort = useMemo(() => {
        const baseColumns = columnDef.map((col) => {
            if (col.field === 'name') {
                return {
                    ...col,
                    renderHeader: () => (
                        <NameColumnHeaderSql
                            sort={sort}
                            onSortChange={onSortChange}
                        />
                    ),
                }
            }
            return col
        })
        return [...referenceRosterColumns, ...baseColumns]
    }, [columnDef, sort, onSortChange, referenceRosterColumns])

    const sortModel: GridSortModel = useMemo(() => {
        if (sort.length === 0) return []
        const firstSort = sort[0]
        if (firstSort.field === 'firstName' || firstSort.field === 'lastName')
            return []
        const sortIndexField = FIELD_TO_SORT_INDEX[firstSort.field]
        if (!sortIndexField) return []
        const col = columnDef.find(
            (c) => 'sortIndexField' in c && c.sortIndexField === sortIndexField
        )
        return col ? [{ field: col.field, sort: firstSort.order }] : []
    }, [sort, columnDef])

    const handleSortModelChange = (model: GridSortModel) => {
        if (model.length === 0) {
            onSortChange([])
            return
        }
        const { field, sort: order } = model[0]
        if (!order) return

        const col = columnDef.find(
            (c) =>
                c.field === field && 'sortIndexField' in c && c.sortIndexField
        )
        if (!col || !('sortIndexField' in col) || !col.sortIndexField) return
        const mappedField =
            SORT_INDEX_TO_FIELD[col.sortIndexField as SortIndexField]
        if (mappedField) {
            onSortChange([{ field: mappedField, order }])
        }
    }

    const handleRowSelectionChange = (model: GridRowSelectionModel) => {
        const rowIds = Array.from(model).map((id) => id.toString())
        setSelectedRowIds(rowIds)
    }

    return (
        <Paper
            elevation={0}
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                paddingX: getPxAsRem(20),
                paddingTop: getPxAsRem(16),
            }}
        >
            <ToolbarSlot
                loading={loading}
                selectionCount={selectedCandidateIds.length}
                totalCount={totalItems}
                roster={roster}
                currentView={currentView}
                selectedCandidateIds={selectedCandidateIds}
                selectedRosterCandidateIds={selectedRosterCandidateIds}
                apiRef={apiRef}
            />
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    overflow: 'hidden',
                }}
            >
                <DataGridPro
                    apiRef={apiRef}
                    rows={data}
                    columns={columnsWithSort}
                    getRowId={generateRowId}
                    columnVisibilityModel={columnVisibilityModel}
                    checkboxSelection
                    keepNonExistentRowsSelected
                    rowSelectionModel={selectedRowIds}
                    onRowSelectionModelChange={handleRowSelectionChange}
                    pagination
                    paginationMode="server"
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    disableMultipleColumnsSorting
                    rowCount={totalItems}
                    paginationModel={{
                        page: page - 1,
                        pageSize: limit,
                    }}
                    onPaginationModelChange={(model) => {
                        if (model.page === 0 && totalItems === 0) {
                            return
                        }
                        onPaginationChange(model)
                    }}
                    pageSizeOptions={[25, 50, 100]}
                    loading={loading || bulkActionLoading}
                    rowHeight={41}
                    columnHeaderHeight={56}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    disableColumnReorder
                    sx={{
                        flex: 1,
                        border: 0,
                        '& .MuiDataGrid-cell': {
                            fontSize: '0.8125rem',
                            borderTop: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        },
                        '& .MuiDataGrid-cell[data-colindex="0"], & .MuiDataGrid-columnHeader[aria-colindex="1"]':
                            {
                                pl: 2,
                            },
                        '& .MuiDataGrid-filler > div': {
                            borderTop: 'none',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            pl: 2,
                        },
                    }}
                    slotProps={{
                        panel: {
                            sx: {
                                '& .MuiDataGrid-columnsManagementHeader': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-columnsManagement': {
                                    padding: 1,
                                },
                                '& .MuiDataGrid-columnsManagementFooter': {
                                    paddingLeft: 1,
                                    '& > label': {
                                        marginLeft: 0,
                                    },
                                },
                            },
                        },
                        columnsPanel: {
                            sx: {
                                '& .MuiDataGrid-columnsManagementRow': {
                                    py: 0.25,
                                    marginLeft: 0,
                                },
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '0.75rem',
                                },
                                '& .MuiCheckbox-root': {
                                    transform: 'scale(0.8)',
                                },
                                '& .MuiButton-root': {
                                    fontSize: '0.6875rem',
                                    textTransform: 'none',
                                    py: 0.5,
                                    px: 1,
                                    minWidth: 'auto',
                                },
                            },
                        },
                    }}
                    pinnedColumns={{
                        left: [
                            GRID_CHECKBOX_SELECTION_COL_DEF.field,
                            'name',
                            'firstName',
                            'lastName',
                            'colors',
                            'rosterCandidateFavoriteCount',
                            'actions',
                            'processStatus',
                        ],
                    }}
                    slots={{
                        toolbar: () => null,
                        footer: () => (
                            <FooterSlot
                                page={page}
                                limit={limit}
                                totalItems={totalItems}
                                onPageChange={(newPage) =>
                                    onPaginationChange({
                                        page: newPage - 1,
                                        pageSize: limit,
                                    })
                                }
                                onLimitChange={(newLimit) =>
                                    onPaginationChange({
                                        page: 0,
                                        pageSize: newLimit,
                                    })
                                }
                            />
                        ),
                    }}
                />
            </Box>
        </Paper>
    )
}
